<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractRentalData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    noLegend: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_1040167445267876_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_1040167445267876_dataSource: b_1040167445267876_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_1040167445267876_dataSource', nodeId: '1040167445267876' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_1040167445267876_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const rentalRequired = computed(() => {
    return (
      ['rental', 'rental_agreement', 'parking'].indexOf(
        props.contract.contract_type
      ) > -1
    );
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.noLegend,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noLegend');
    }
  );

  const act_1040167445267876_setData_r7779rqb9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract.rental',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_qj321elmz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract.rental',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_request_ouid3wu1t_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_rental',
      parameters: {
        id: $event.data.value.id,
      },
      headers: {
        accept: 'application/vnd.itp.rentalparty+json',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_at87lx5ox =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_a7mqrh4iy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_at87lx5ox,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_a7mqrh4iy],
    event: 'reload',
  });
  const act_1040167445267876_setData_r7779rqb9 = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_r7779rqb9_getActionArgs,
  });
  const inst_evh_1040167445267876_optionDeselected_7nma4acvy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_r7779rqb9,
      event: 'optionDeselected',
      displayName: 'setData',
    });
  const inst_evh_1040167445267876_optionDeselected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_optionDeselected_7nma4acvy],
      event: 'optionDeselected',
    });
  const act_1040167445267876_setData_qj321elmz = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_qj321elmz_getActionArgs,
  });
  const inst_evh_1040167445267876_success_v9cr5qui3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_qj321elmz,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_request_ouid3wu1t = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_ouid3wu1t_getActionArgs,
    events: [inst_evh_1040167445267876_success_v9cr5qui3],
  });
  const inst_evh_1040167445267876_optionSelected_kllvb32p5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_ouid3wu1t,
      event: 'optionSelected',
      displayName: 'request',
    });
  const inst_evh_1040167445267876_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_optionSelected_kllvb32p5],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_a7mqrh4iy,
    inst_evh_7315092382398562_reload,
    inst_evh_1040167445267876_optionDeselected_7nma4acvy,
    inst_evh_1040167445267876_optionDeselected,
    inst_evh_1040167445267876_success_v9cr5qui3,
    inst_evh_1040167445267876_optionSelected_kllvb32p5,
    inst_evh_1040167445267876_optionSelected,
  };

  const evh_7315092382398562_reload_a7mqrh4iy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_a7mqrh4iy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionDeselected_7nma4acvy = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_optionDeselected_7nma4acvy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionDeselected = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_optionDeselected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_v9cr5qui3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_v9cr5qui3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected_kllvb32p5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_optionSelected_kllvb32p5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_optionSelected = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend v-if="!!noLegend">
      {{ pgettext('Contract', 'Rental') }}
    </legend>
    <ItpFormGroup
      name="rental"
      :label="pgettext('Rental', 'Rental')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      :required="rentalRequired"
    >
      <ItpFormSearchableSelect
        name="rental"
        :required="rentalRequired"
        v-model="contract.rental"
        bind-object
        :dataSource="b_1040167445267876_dataSource"
        textField="number,name"
        clearable
        optionTemplate="<UcSuggestRentalOption :option=option></UcSuggestRentalOption>"
        use-option-template-for-selected-option
        @optionDeselected="evh_1040167445267876_optionDeselected($event, {})"
        @optionSelected="evh_1040167445267876_optionSelected($event, {})"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <div v-if="contract.rental && contract.rental.id">
      <ItpFormGroup
        name="name"
        :label="pgettext('Rental', 'Name')"
        :label-cols-md="4"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <ItpFormInput name="name" v-model="contract.rental.name">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="name"
        :label="pgettext('Rental', 'Address')"
        :label-cols-md="4"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <ItpFormInput name="name" v-model="contract.rental.address">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="area"
        :label="pgettext('Rental', 'Area')"
        :label-cols-md="4"
        :content-cols-md="4"
        :labelAlign="labelAlign"
      >
        <ItpFormInput name="name" v-model="contract.rental.area">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="lot_number"
        :label="pgettext('Rental', 'LOT number')"
        :label-cols-md="4"
        :content-cols-md="4"
        :labelAlign="labelAlign"
      >
        <ItpFormInput name="name" v-model="contract.rental.lot_number">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="house_number"
        :label="pgettext('Rental', 'House number')"
        :label-cols-md="4"
        :content-cols-md="4"
        :labelAlign="labelAlign"
      >
        <ItpFormInput name="name" v-model="contract.rental.house_number">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="description"
        :label="pgettext('Rental', 'Description')"
        :label-cols-md="4"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <ItpFormTextarea name="name" v-model="contract.rental.description">
        </ItpFormTextarea>
      </ItpFormGroup>
    </div>
  </ItpUsercontrol>
</template>
